import {inject, Injectable, signal, WritableSignal} from '@angular/core'
import {BehaviorSubject, Observable, of} from 'rxjs'
import {CognitoIdToken} from 'amazon-cognito-identity-js'
import {CognitoService} from './cognito.service'
import {environment} from '../../environments/environment'
import {catchError, first, map, switchMap, tap} from 'rxjs/operators'
import {UserService} from './user.service'
import {IKulladalUser} from '@kdl/kulladal-product-backend'
import {Router} from '@angular/router'
import {LOCAL_STORAGE} from '../application/injection-tokens'
import {ROUTE_LOGIN} from '../application/constants'
import {Ti18nKey} from '../i18n/i18n-keys'

/**
 * This enum is for communicating the internal Cognito state.
 */
export enum EAuthState {
  start, // Login started on not started, wait for username/password ish
  code, // Verification code needed
  newPassword,// Need to set a new password, for new users
  resetPassword, // When password is reset, user must log in.
  pendingUser,
  authenticated,// User is authenticated and ready to go.
  passwordUpdated, // Successful password update done
  error// Something is wrong
}

export const ADMIN_ROLE_NAME = 'admin'
export const CREW_ROLE_NAME = 'crew'
export const AGENT_ROLE_NAME = 'agent'

export interface IAuthInfo {

  /**
   * The "access token" that we send with requests to API gw.
   */
  accessToken?: string

  /**
   * The current state, see IAuthState
   */
  state: EAuthState

  /**
   * Random data depending on state.
   */
  data?: any

  /**
   * Error information, if any
   */
  error?: Ti18nKey

  /**
   * The complete user, with roles and everything
   * This is populated by the AuthService as soon as
   * we (potentially) have authenticated.... ?
   */
  user?: IKulladalUser
}

/**
 * Abstracts the Authentication stuff for the rest of the application.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  /**
   * Should be a subscription-able string of the login state
   */
  public authState$: Observable<IAuthInfo>

  public isSuperAdmin: WritableSignal<boolean> = signal<boolean>(false)
  public isAdmin: WritableSignal<boolean> = signal<boolean>(false)
  public isCrew: WritableSignal<boolean> = signal<boolean>(false)
  public isAgent: WritableSignal<boolean> = signal<boolean>(false)
  public isAgentOnly: WritableSignal<boolean> = signal<boolean>(false)

  /**
   * This is available in user service as well, but
   * we want this here to avoid heavy dependencies on HTTP
   */
  public currentUser$: WritableSignal<IKulladalUser | null> = signal<IKulladalUser | null>(null)

  /**
   * Internal representation that kan have next etc.
   */
  private readonly pAuthState$ = new BehaviorSubject<IAuthInfo>({
    state: EAuthState.start
  })

  private readonly injectedLocalStorage: Storage = inject(LOCAL_STORAGE)
  private readonly cognitoService: CognitoService = inject(CognitoService)
  private readonly userService: UserService = inject(UserService)
  private readonly router: Router = inject(Router)

  constructor() {
    this.authState$ = this.pAuthState$.asObservable()
  }

  /**
   * Checks if a user login is possible, if so updates the user,
   * If not, reset token and login.
   *
   * This is called by a) the bootstrap, b) the 401 error handler.
   */
  public checkLogin(): Observable<boolean> {
    // Use-case for no-connection times:
    // const fakeUser = {
    //   email: 'dario@kulladal.com',
    //   name: 'Darío Yuste Tirados',
    //   roles: []
    // }
    // this.pAuthState$.next({
    //   state: EAuthState.authenticated,
    //   user: fakeUser
    // })
    // this.isSuperAdmin.set(true)
    // this.isAdmin.set(true)
    // this.currentUser$.set(fakeUser)
    // return of(true)

    // Check if we have the data stored if so we try to log 'em in.
    const username = this.injectedLocalStorage
      .getItem(`CognitoIdentityServiceProvider.${environment.clientId}.LastAuthUser`)
    const user = this.cognitoService.getExistingUser(username)
    // If cognito reports a user, we try to refresh tokens for it
    if (user) {
      return this.updateStateWithToken()
    }

    this.pAuthState$.next({
      state: EAuthState.start
    })
    // Else we just resolve and let us end up on login.
    this.router.navigate([ROUTE_LOGIN]).then()
    return of(false)
  }

  public login(username: string, password: string): Observable<EAuthState> {
    return this.cognitoService.login(username, password).pipe(
      map((state: EAuthState) => {
          if (state === EAuthState.error) {
            this.pAuthState$.next({
              state,
              error: 'login_error_wrong'
            })
          }
          if (state === EAuthState.newPassword) {
            this.pAuthState$.next({
              state
            })
          }
          if (state === EAuthState.resetPassword) {
            this.pAuthState$.next({
              state
            })
          }
          if (state === EAuthState.pendingUser) {
            this.updateStateWithToken().subscribe()
          }
          return state
        }
      ))
  }

  /**
   * Send logout to cognito and re-initialize the auth loop.
   */
  public logout(): Observable<void> {
    return this.cognitoService.logout().pipe(
      map(() => {
        this.pAuthState$.next({
          state: EAuthState.start
        })
      })
    )
  }

  /**
   * When set password succeeeds the user is logged in.
   *
   * @param newPassword
   */
  public setPassword(newPassword: string): Observable<boolean> {
    return this.cognitoService.setPassword(newPassword).pipe(
      switchMap((res: EAuthState) => {
          if (res === EAuthState.authenticated) {
            return this.updateStateWithToken()
          }
          this.pAuthState$.next({
            state: EAuthState.error,
            error: 'login_error_no_password'
          })
          return of(false)
        }
      )
    )
  }

  public resetPassword(username: string, newPassword: string, code: string): Observable<any> {
    return this.cognitoService.confirmPassword(newPassword, code).pipe(
      switchMap((res: EAuthState) => {
        // Cognito service
        if (res === EAuthState.passwordUpdated) {
          return this.login(username, newPassword)
        }
        this.pAuthState$.next({
          state: EAuthState.error,
          error: 'login_error_no_password'
        })
        return of(false)
      })
    )
  }

  public forgotPassword(username: string): void {
    this.cognitoService.forgotPassword(username).subscribe({
      next: (res: EAuthState) => {
        if (res === EAuthState.passwordUpdated) {
          return this.pAuthState$.next({
            state: EAuthState.resetPassword
          })
        }
        this.pAuthState$.next({
          state: EAuthState.error,
          error: 'login_error_no_password'
        })
      }
    })
  }

  private updateStateWithToken(): Observable<boolean> {
    const user = this.cognitoService.user
    // This is what REALLY checks stuff on the internet.
    return this.cognitoService.getExistingSession(user)
      .pipe(
        switchMap((res: CognitoIdToken | boolean) => {
          if (res === false) {
            this.pAuthState$.next({
              state: EAuthState.start
            })
            // Make sure to take the user to the login page
            this.router.navigate([ROUTE_LOGIN]).then()
            return of(res as boolean)
          } else {
            this.pAuthState$.next({
              state: EAuthState.pendingUser,
              accessToken: this.cognitoService.getUserJwt(res as CognitoIdToken)
            })
            // This may return an error!
            return this.getUserSelfForFun()
          }
        }),
        catchError(() => {
          // Make sure to log out and navigate to log in.
          this.cognitoService.logout().subscribe({
            next: (state: EAuthState) => {
              this.pAuthState$.next({
                state
              })
            }
          })
          this.router.navigate([ROUTE_LOGIN]).then()
          return of(false)
        })
      )
  }

  /**
   * Whenever we think we want to, we go fetch the user...
   *
   */
  private getUserSelfForFun(): Observable<boolean> {
    let user: IKulladalUser
    return this.userService.getUserSelf()
      .pipe(
        tap((self: IKulladalUser) => user = self),
        switchMap(() => this.pAuthState$),
        first(), // Take just one, because we will emit here just after!
        map((state: IAuthInfo) => {
          state.state = EAuthState.authenticated
          state.user = user
          this.isAdmin.set(user.roles.includes(ADMIN_ROLE_NAME))
          this.isSuperAdmin.set(this.isAdmin() && user.email === 'dario@kulladal.com')
          this.isCrew.set(user.roles.includes(CREW_ROLE_NAME))
          this.isAgent.set(user.roles.includes(AGENT_ROLE_NAME))
          this.isAgentOnly.set(this.isAgent() && user.roles.length === 1)
          this.currentUser$.set(user)
          this.pAuthState$.next(state)
          return true
        })
      )
  }
}
